import { useEffect, useRef, useState } from "react";
import classNames from "classnames";

import {
  FILTER_INDENT_MARGIN_PX,
  FILTER_ROW_OPACITY_PERCENTAGE_INCREASE,
} from "features/ui/Filters/FilterBuilder/constants";
import { FilterRowState } from "features/ui/Filters/FilterBuilder/types";
import { FilterOperator } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import AttributeSelect from "./AttributeSelect";
import IncompleteRangeHint from "./IncompleteRangeHint";
import OperatorSelect from "./OperatorSelect";
import RowActions from "./RowActions";
import { OnRowDataChangeHandle } from "./types";
import ValuesSelect from "./ValuesSelect";

interface Props extends FilterRowState {
  onNewRow: (id: string) => void;
  onNewGroup: (id: string) => void;
  onDelete?: (id: string) => void;
  onRowDataChange: OnRowDataChangeHandle;
  schema: SchemaEntry<string>[];
  disabled?: boolean;
  attributePlaceholder?: string;
  embedded?: boolean;
  depth: number;
  occursFilterSelector?: boolean;
  hasInitialFocus?: boolean;
  disableGroups?: boolean;
}

const FilterRow = ({
  id,
  attribute,
  operator,
  values,
  depth,
  onNewRow,
  onNewGroup,
  onDelete,
  onRowDataChange,
  schema,
  disabled,
  attributePlaceholder,
  embedded,
  occursFilterSelector,
  hasInitialFocus,
  disableGroups,
}: Props) => {
  const [hasInputFocus, setHasInputFocus] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  const handleFocus = () => {
    // Clear any pending blur timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setHasInputFocus(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Only set a timeout to blur if we're not focusing another element within the same row
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      timeoutRef.current = setTimeout(() => {
        setHasInputFocus(false);
      }, 50); // Small delay to allow for the next focus to occur
    }
  };

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const currentAttributeSchema: SchemaEntry<string> | undefined = attribute
    ? schema.find((s) => s.filter?.fieldName === attribute)
    : undefined;

  // If operator is "exists" or "occurs" we need to add a background color to the row to make both operator and sub-filters stand out
  const isEmbeddedOperator =
    operator &&
    [
      FilterOperator.EXISTS,
      FilterOperator.OCCURS,
      FilterOperator.NOT_EXISTS,
      FilterOperator.NOT_OCCURS,
    ].includes(operator);

  const filterIndent = FILTER_INDENT_MARGIN_PX;
  const rowOpacity = isEmbeddedOperator
    ? (depth + 1) * FILTER_ROW_OPACITY_PERCENTAGE_INCREASE
    : 0;

  return (
    <div
      style={{
        marginLeft: filterIndent - (isEmbeddedOperator ? 4 : 0) + "px",
        background: `rgba(0,0,0, ${rowOpacity}%)`,
        borderRadius: isEmbeddedOperator ? "4px" : undefined,
      }}
    >
      <div
        key={id}
        id={id}
        data-testid="filter-row"
        className={classNames("flex items-center gap-2 mt-2 group/row", {
          "pl-1 pr-1 pt-1 mb-2": isEmbeddedOperator,
        })}
        onFocusCapture={handleFocus}
        onBlurCapture={handleBlur}
      >
        {/* Filter attribute */}
        <AttributeSelect
          selected={attribute}
          onChange={(selected) =>
            onRowDataChange(id, "attribute", selected, values)
          }
          schema={schema}
          disabled={disabled}
          attributePlaceholder={attributePlaceholder}
          openOnAutoFocus={true}
          hasInitialFocus={hasInitialFocus}
          isInsideFilterBuilder={true}
        />
        {attribute && (
          <OperatorSelect
            autoFocus={
              operator === null || operator === FilterOperator.NOT_FILTERED
            }
            selected={operator}
            onChange={(selected) =>
              onRowDataChange(id, "operator", selected, values)
            }
            attributeSchema={currentAttributeSchema}
            disabled={disabled}
            isInsideFilterBuilder={true}
          />
        )}
        {/* Filter values */}
        {operator &&
          operator !== FilterOperator.NOT_FILTERED &&
          !isEmbeddedOperator && (
            <ValuesSelect
              occursFilterSelector={occursFilterSelector}
              autoFocus={values === null || values.length === 0}
              attribute={attribute}
              operator={operator}
              values={values}
              schema={currentAttributeSchema}
              onChange={(newValues) =>
                onRowDataChange(id, "values", newValues, values)
              }
              disabled={disabled}
              isInsideFilterBuilder={true}
            />
          )}
        {/* Filter actions */}
        <RowActions
          id={id}
          onNewRow={onNewRow}
          onNewGroup={onNewGroup}
          onDelete={onDelete}
          disabled={disabled}
          hideAddGroupCta={embedded || disableGroups}
          hasInputFocus={hasInputFocus}
        />
      </div>
      <IncompleteRangeHint
        operator={operator}
        values={values}
        className="ml-1"
      />
      {isEmbeddedOperator && (
        <div className="ml-8">
          <ValuesSelect
            occursFilterSelector={occursFilterSelector}
            autoFocus={values === null || values.length === 0}
            attribute={attribute}
            operator={operator}
            values={values}
            schema={currentAttributeSchema}
            onChange={(newValues) =>
              onRowDataChange(id, "values", newValues, values)
            }
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default FilterRow;
